import { store } from '../redux/store';
import httpService, { setToken, wipeToken, setRefreshToken } from './httpService';
import Community, { CommunityType } from '../types/community';
import Business, { BusinessType } from '../types/business';
import { CreateCommunityForm, CreateBusinessForm, CreateUserForm } from '../types/forms';
import {
  login,
  setSelectedCommunity,
  logout as logoutDispatch,
  setSelectedDashboard,
  setSelectedBusiness,
} from '../redux/slices/applicationSlice';
import { RegisterRequest, RegisterResponseBody } from '../models/authService.model';

/*
 * The auth service, contains all logic for authentication
 */
const authService = {
  /*
   * Registers a new user
   * @param {UserType} user - The user to register
   * @returns {AxiosResponse} - The response from the server
   */
  register: async (alias: string, email: string): Promise<void> => {
    const body: RegisterRequest = { alias, email };

    return httpService
      .post<RegisterResponseBody>('/auth/register', body)
      .then(({ data: { userId, accessToken, refreshToken } }): void => {
        setToken(accessToken);
        setRefreshToken(refreshToken);
        const [firstName, lastName] = alias.split('+');

        store.dispatch(
          login({
            userId,
            firstName,
            lastName,
            email,
          }),
        );
      });
  },

  instantRegisterCommunity: async (user: CreateUserForm, community: CreateCommunityForm): Promise<void> => {
    const alias = `${user.firstName}+${user.lastName}`;
    const {
      data: { accessToken, refreshToken, userId, userAlias, userEmail },
    } = await httpService.post<RegisterResponseBody>('/auth/register?source=community-dashboard', {
      email: user.email,
      alias,
      afterAction: 'create-community',
      afterActionData: {
        name: community.name,
        description: community.description,
        connectedMembers: community.connectedMembers,
        type: community.type,
        features: community.features,
        affiliateCode: community.affiliateCode,
        subscriptionType: community.subscriptionType,
      },
    });

    const firstName = userAlias?.split('+')[0] || user.firstName || '';
    const lastName = userAlias?.split('+')[1] || user.lastName || '';
    setToken(accessToken);
    setRefreshToken(refreshToken);
    const { data: communities } = await httpService.get<Community[]>('/networks/communities/@me');
    store.dispatch(
      login({
        userId,
        firstName,
        lastName,
        email: userEmail,
        communities,
      }),
    );
    if (communities[0]?.id) store.dispatch(setSelectedCommunity(communities[0].id));
    if (communities[0]?.dashboard?.dashboardId) store.dispatch(setSelectedDashboard(communities[0].dashboard?.dashboardId));
  },

  instantRegisterBusiness: async (user: CreateUserForm, business: CreateBusinessForm): Promise<void> => {
    const alias = `${user.firstName}+${user.lastName}`;

    const mainCommunityType = 
      BusinessType.SPORTCLUB ? CommunityType.SPORTTEAM :  
      BusinessType.STUDENTASSOCIATION ? CommunityType.ASSOCIATION : 
      CommunityType.OTHER_COMMUNITY;

    const {
      data: { accessToken, refreshToken, userId },
    } = await httpService.post<RegisterResponseBody>('/auth/register', {
      email: user.email,
      alias,
      afterAction: 'create-business',
      afterActionData: {
        name: business.name,
        description: business.description,
        type: business.type,
        affiliateCode: business.affiliateCode,
        subscriptionType: business.subscriptionType,
        dashboardFeatures: business.dashboardFeatures,
        community: {
          name: business.name,
          description: business.description || '',
          type: mainCommunityType,
          enableDashboard: true,
          features: business.dashboardFeatures,
        }
      },
    });

    const [firstName, lastName] = alias.split('+');
    setToken(accessToken);
    setRefreshToken(refreshToken);
    const { data: communities } = await httpService.get<Community[]>('/networks/communities/@me');
    const { data: businesses } = await httpService.get<Business[]>('/networks/businesses');
    store.dispatch(
      login({
        userId,
        firstName,
        lastName,
        email: user.email || '',
        communities,
        businesses,
      }),
    );
    if (businesses[0]?.businessId) {
      store.dispatch(setSelectedBusiness(businesses[0].businessId));
      if (businesses[0]?.dashboard?.dashboardId) {
        store.dispatch(setSelectedDashboard(businesses[0].dashboard?.dashboardId));
      }
    }
  },

  generateMagicLink: async (email: string): Promise<void> => {
    const isInBusinessMode = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';
    await httpService.post('/auth/magic-link', { email }, {
      params: {
        source: isInBusinessMode ? 'business-dashboard' : 'community-dashboard',
        redirectUrl: '/onboarding',
      }
    });
  },

  /*
   * Logs in a user from a magic link.
   *
   * @param {token} string - The user to login
   * @returns {void}
   */
  loginMagicLink: async (magicLinkId: string): Promise<void> => {
    const {
      data: { accessToken, refreshToken, userId, userAlias, userEmail, userRole: role },
    } = await httpService.post('/auth/login', { magicLinkToken: magicLinkId });
    const [firstName, lastName] = userAlias.split('+');
    setToken(accessToken);
    setRefreshToken(refreshToken);
    const { data: communities } = await httpService.get<Community[]>('/networks/communities/@me');
    const { data: businesses } = await httpService.get<Business[]>('/networks/businesses');
    store.dispatch(logoutDispatch());
    store.dispatch(
      login({
        userId,
        firstName,
        lastName,
        email: userEmail,
        communities,
        businesses,
        role,
      }),
    );
    
    // Set selected business if user has businesses
    if (businesses.length > 0 && businesses[0]?.businessId) {
      store.dispatch(setSelectedBusiness(businesses[0].businessId));
      if (businesses[0]?.dashboard?.dashboardId) {
        store.dispatch(setSelectedDashboard(businesses[0].dashboard?.dashboardId));
      }
    }
  },

  /*
   * Logs out a user
   * @returns {void}
   */
  logout: (): void => {
    store.dispatch(logoutDispatch());
    wipeToken();
    localStorage.clear();
  },
};

export default authService;
